import React, { useState, useEffect } from 'react';
import { Card, Button, plain, inputClass, selectClass, channelOptions } from './dashboardShared';

// The dashboard's Server tab: the welcome message and its Verified and Whatnot roles, and the Whatnot extras (the
// schedule board, the Live Now voice channel, the control panel and the shop board). Each change runs the cog's own
// command (?welcome …, ?wnlive …), so it's checked the same way and still works from Discord too.

const WELCOME_TEXTS = [
  { field: 'title', label: 'Heading', multi: false, help: 'The big heading at the top of the welcome message.' },
  { field: 'intro', label: 'Text under the heading', multi: true, help: 'Write {bot} where TOGΞ should be mentioned, and {dev} for you.' },
  { field: 'banner', label: 'Banner image link', multi: false, help: 'A link to an image. Leave it empty for no banner.' },
  { field: 'docs', label: 'Guide button link', multi: false, help: 'Where the Guide button goes. Leave it empty to remove the button.' },
];

const BOARDS = [
  { what: 'schedule', title: '📅 Schedule board', help: "Today's shows from everyone on the Whatnot list, kept up to date." },
  { what: 'panel', title: '🎛️ Control panel', help: 'The buttons for adding and removing Whatnot sellers.' },
  { what: 'shop', title: '🛍️ Shop board', help: "One seller's Whatnot shop, kept up to date.", user: true },
];

export default function ServerTab({ ask, lists, setNote, failed }) {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [picks, setPicks] = useState({});   // channels (and the shop's seller) picked but not posted yet
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState('');

  const load = async () => {
    const answer = await ask('setup.get');
    if (!failed(answer)) setData(answer.data || {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (action, args, label) => {
    setBusy(label); setNote(null);
    const answer = await ask(action, args, 60000);
    setBusy('');
    if (failed(answer)) return false;
    setNote({ text: plain(answer.data.message, lists.channels, lists.roles), ok: answer.data.ok });
    if (answer.data.ok) load();
    return answer.data.ok;
  };

  const channelName = (id) => (id ? `#${lists.channels.find(c => c.id === id)?.name || 'deleted channel'}` : null);
  if (!data) return <p className="text-zinc-500 text-xs">Loading…</p>;
  const welcome = data.welcome;
  const whatnot = data.whatnot;

  return (
    <div className="space-y-4">
      {!welcome ? (
        <Card title="Welcome message"><p className="text-zinc-500 text-xs">The welcome cog isn't loaded. Load it in Overview first.</p></Card>
      ) : (<>
        <Card title="Verification roles">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[['verifiedrole', 'verifiedRole', 'Verified role', 'Unlocks the server.'],
              ['whatnotrole', 'whatnotRole', 'Whatnot role', 'Given with it when someone proves their Whatnot account.']].map(([field, key, label, help]) => (
              <label key={field} className="block">
                <span className="block text-zinc-300 text-[11px] font-semibold mb-1">{label}</span>
                <select value={welcome[key] || ''} disabled={!!busy} onChange={e => e.target.value && run('setup.welcome', { field, value: e.target.value }, field)}
                  className={`w-full ${selectClass}`}>
                  <option value="" disabled>Pick a role</option>
                  {lists.roles.map(r => <option key={r.id} value={r.id}>@{r.name}</option>)}
                </select>
                <span className="block text-zinc-500 text-[10px] mt-1">{help}</span>
              </label>
            ))}
          </div>
        </Card>

        <Card title="Welcome message" right={welcome.channel ? <span className="text-zinc-500 text-[11px]">in {channelName(welcome.channel)}</span> : null}>
          <div className="space-y-3">
            {WELCOME_TEXTS.map(({ field, label, multi, help }) => {
              const draft = drafts[field] ?? welcome[field];
              const Input = multi ? 'textarea' : 'input';
              return (
                <label key={field} className="block">
                  <span className="block text-zinc-300 text-[11px] font-semibold mb-1">{label}</span>
                  <span className="flex flex-col sm:flex-row gap-2 sm:items-start">
                    <Input value={draft} rows={multi ? 4 : undefined} onChange={e => setDrafts(d => ({ ...d, [field]: e.target.value }))}
                      className={`flex-1 ${inputClass} ${multi ? 'resize-y' : ''}`} />
                    <Button tone="primary" small disabled={!!busy || draft === welcome[field]}
                      onClick={async () => { if (await run('setup.welcome', { field, value: draft }, field)) setDrafts(d => ({ ...d, [field]: undefined })); }}>
                      {busy === field ? 'Saving…' : 'Save'}
                    </Button>
                  </span>
                  <span className="block text-zinc-500 text-[10px] mt-1">{help}</span>
                </label>
              );
            })}
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <select value={picks.welcome || ''} onChange={e => setPicks(p => ({ ...p, welcome: e.target.value }))} className={`flex-1 ${selectClass}`}>
                <option value="">{welcome.channel ? 'Move it to which channel?' : 'Post it in which channel?'}</option>
                {channelOptions(lists.channels)}
              </select>
              <Button disabled={!!busy || !picks.welcome} onClick={() => run('setup.welcome', { field: 'post', value: picks.welcome }, 'post')}>
                {busy === 'post' ? 'Posting…' : welcome.channel ? 'Post again there' : 'Post'}
              </Button>
            </div>
            <p className="text-zinc-500 text-[10px]">Saved changes update the posted message straight away. Posting again replaces the old one.</p>
          </div>
        </Card>
      </>)}

      {!whatnot ? (
        <Card title="Whatnot extras"><p className="text-zinc-500 text-xs">The whatnotlive cog isn't loaded. Load it in Overview first.</p></Card>
      ) : (<>
        {BOARDS.map(({ what, title, help, user }) => (
          <Card key={what} title={title} right={whatnot[what] ? <span className="text-zinc-500 text-[11px]">in {channelName(whatnot[what])}{user && whatnot.shopUser ? ` · @${whatnot.shopUser}` : ''}</span> : <span className="text-zinc-600 text-[11px]">not posted</span>}>
            <p className="text-zinc-400 text-xs mb-2">{help}</p>
            <div className="flex flex-col sm:flex-row gap-2">
              {user && (
                <input value={picks.shopUser ?? whatnot.shopUser} onChange={e => setPicks(p => ({ ...p, shopUser: e.target.value }))}
                  placeholder="Whatnot username" className={`sm:w-44 ${inputClass}`} />
              )}
              <select value={picks[what] || ''} onChange={e => setPicks(p => ({ ...p, [what]: e.target.value }))} className={`flex-1 ${selectClass}`}>
                <option value="">{whatnot[what] ? 'Move it to which channel?' : 'Post it in which channel?'}</option>
                {channelOptions(lists.channels)}
              </select>
              <Button disabled={!!busy || !picks[what] || (user && !(picks.shopUser ?? whatnot.shopUser))}
                onClick={() => run('setup.whatnot', { what, channel: picks[what], username: picks.shopUser ?? whatnot.shopUser }, what)}>
                {busy === what ? 'Posting…' : whatnot[what] ? 'Post again there' : 'Post'}
              </Button>
            </div>
          </Card>
        ))}

        <Card title="🔴 Live Now voice channel" right={
          whatnot.stats ? (
            <Button small tone={confirm === 'stats' ? 'danger' : 'ghost'} disabled={!!busy}
              onClick={() => { if (confirm !== 'stats') { setConfirm('stats'); return; } setConfirm(''); run('setup.whatnot', { what: 'statsremove' }, 'stats'); }}>
              {busy === 'stats' ? '…' : confirm === 'stats' ? 'Sure? Delete it' : 'Delete channel'}
            </Button>
          ) : (
            <Button small tone="primary" disabled={!!busy} onClick={() => run('setup.whatnot', { what: 'stats' }, 'stats')}>
              {busy === 'stats' ? 'Creating…' : 'Create channel'}
            </Button>
          )
        }>
          <p className="text-zinc-400 text-xs">
            {whatnot.stats ? <>Showing as <b className="text-white">{whatnot.stats}</b>. </> : 'Not set up. '}
            A locked voice channel whose name counts how many Whatnot sellers are live. It's made at the top of the channel list: drag it wherever you like.
          </p>
        </Card>
      </>)}
    </div>
  );
}
